// Reusable upload metadata dialog. Consumers stage a file through the
// `useUploadMetadataDialog` hook, the dialog asks the user for Name +
// Description + Uploaded by, then calls back with the populated values.
//
// The actual upload pipeline (presign → PUT → register) is owned by
// the consumer. This component only collects metadata; it doesn't do
// the network work.

import { useState } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

export interface UploadMetadata {
  name: string
  description: string
  uploadedBy: string
}

export interface PendingFileUpload {
  file: File
  // Optional title shown in the dialog header (e.g. the artifact kind).
  kindLabel?: string
}

interface UploadMetadataDialogProps {
  pending: PendingFileUpload | null
  onCancel: () => void
  // Async so the dialog can show a spinner during the actual upload.
  // Throwing keeps the dialog open with the entered values intact.
  onConfirm: (file: File, meta: UploadMetadata) => Promise<void> | void
}

export function UploadMetadataDialog(props: UploadMetadataDialogProps) {
  // Re-mount the inner form whenever a new file is staged so the
  // useState initialisers pick up the file's name without a setState-
  // in-effect dance.
  const fileKey = props.pending
    ? `${props.pending.file.name}-${props.pending.file.size}`
    : "empty"
  return <UploadMetadataDialogInner key={fileKey} {...props} />
}

function UploadMetadataDialogInner({
  pending,
  onCancel,
  onConfirm,
}: UploadMetadataDialogProps) {
  const { t } = useTranslation("transit")
  const [name, setName] = useState(pending?.file.name ?? "")
  const [description, setDescription] = useState("")
  const [uploadedBy, setUploadedBy] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    if (!pending) return
    const trimmedName = name.trim()
    if (!trimmedName) return
    setSubmitting(true)
    try {
      await onConfirm(pending.file, {
        name: trimmedName,
        description: description.trim(),
        uploadedBy: uploadedBy.trim(),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={!!pending}
      onOpenChange={(next) => {
        if (!next && !submitting) onCancel()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {pending?.kindLabel
              ? t("uploadMeta.titleWithKind", { kind: pending.kindLabel })
              : t("uploadMeta.title")}
          </DialogTitle>
          <DialogDescription>{t("uploadMeta.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-meta-name">
              {t("uploadMeta.nameLabel")}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="upload-meta-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("uploadMeta.namePlaceholder")}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-meta-description">
              {t("uploadMeta.descriptionLabel")}
            </Label>
            <Textarea
              id="upload-meta-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("uploadMeta.descriptionPlaceholder")}
              rows={3}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-meta-author">
              {t("uploadMeta.uploadedByLabel")}{" "}
              <span className="text-muted-foreground font-normal">
                {t("uploadMeta.optionalSuffix")}
              </span>
            </Label>
            <Input
              id="upload-meta-author"
              value={uploadedBy}
              onChange={(e) => setUploadedBy(e.target.value)}
              placeholder={t("uploadMeta.uploadedByPlaceholder")}
            />
          </div>

          {pending ? (
            <p className="text-muted-foreground text-[11px]">
              {t("uploadMeta.fileLabel")}:{" "}
              <span className="font-mono break-all">{pending.file.name}</span>
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            {t("uploadMeta.cancel")}
          </Button>
          <Button onClick={submit} disabled={!name.trim() || submitting}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("uploadMeta.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
